import { Link, useLocation } from 'react-router-dom';
import { useState } from 'react';
import { useQuery } from 'react-query';
import { contentAPI } from '../../services/api';
import {
  Box,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  IconButton,
  Divider,
  Chip,
  Tooltip,
  useMediaQuery
} from '@mui/material';
import DashboardRoundedIcon from '@mui/icons-material/DashboardRounded';
import HikingRoundedIcon from '@mui/icons-material/HikingRounded';
import CategoryRoundedIcon from '@mui/icons-material/CategoryRounded';
import PlaceRoundedIcon from '@mui/icons-material/PlaceRounded';
import EventAvailableRoundedIcon from '@mui/icons-material/EventAvailableRounded';
import GroupRoundedIcon from '@mui/icons-material/GroupRounded';
import GroupsRoundedIcon from '@mui/icons-material/GroupsRounded';
import InfoRoundedIcon from '@mui/icons-material/InfoRounded';
import ArticleRoundedIcon from '@mui/icons-material/ArticleRounded';
import ContactMailRoundedIcon from '@mui/icons-material/ContactMailRounded';
import ShortTextRoundedIcon from '@mui/icons-material/ShortTextRounded';
import SecurityRoundedIcon from '@mui/icons-material/SecurityRounded';
import HistoryRoundedIcon from '@mui/icons-material/HistoryRounded';
import ChevronLeftRoundedIcon from '@mui/icons-material/ChevronLeftRounded';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import MenuRoundedIcon from '@mui/icons-material/MenuRounded';
import WorkRoundedIcon from '@mui/icons-material/WorkRounded';
import PersonSearchRoundedIcon from '@mui/icons-material/PersonSearchRounded';
import PhoneCallbackRoundedIcon from '@mui/icons-material/PhoneCallbackRounded';
import NoteAddRoundedIcon from '@mui/icons-material/NoteAddRounded';
import AssessmentRoundedIcon from '@mui/icons-material/AssessmentRounded';
import SupportAgentRoundedIcon from '@mui/icons-material/SupportAgentRounded';
import ReceiptLongRoundedIcon from '@mui/icons-material/ReceiptLongRounded';
import ConfirmationNumberRoundedIcon from '@mui/icons-material/ConfirmationNumberRounded';
import CancelScheduleSendRoundedIcon from '@mui/icons-material/CancelScheduleSendRounded';
import ShoppingBagRoundedIcon from '@mui/icons-material/ShoppingBagRounded';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import LocalShippingRoundedIcon from '@mui/icons-material/LocalShippingRounded';
import HolidayVillageRoundedIcon from '@mui/icons-material/HolidayVillageRounded';
import LinkRoundedIcon from '@mui/icons-material/LinkRounded';
import HowToRegRoundedIcon from '@mui/icons-material/HowToRegRounded';
import SchoolRoundedIcon from '@mui/icons-material/SchoolRounded';
import { useTheme } from '@mui/material/styles';
import { useAuth } from '../../context/AuthContext';
import { brand } from '../../theme';

const Sidebar = ({ isCollapsed, onToggle }) => {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('lg'));
  const location = useLocation();
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const { can, user, isSuperAdmin } = useAuth();
  const { data: siteBranding } = useQuery(
    ['site-settings-branding'],
    async () => {
      const res = await contentAPI.getSiteSettings();
      return res.data?.data ?? res.data ?? {};
    },
    { staleTime: 5 * 60 * 1000, retry: 1 }
  );
  const brandName =
    String(siteBranding?.site_brand_name || 'INVINCIBLE').trim() || 'INVINCIBLE';

  const drawerWidth = isCollapsed ? 80 : 268;
  const isSuper = isSuperAdmin || user?.role === 'super_admin' || user?.role?.toLowerCase()?.replace(/[\s-]+/g, '_') === 'super_admin';

  const menuSections = [
    {
      id: 'main',
      title: 'Main Menu',
      items: [
        { path: '/', label: 'Dashboard', icon: <DashboardRoundedIcon fontSize="small" />, permission: 'dashboard' },
        { path: '/custom-reports', label: 'Custom Reports', icon: <AssessmentRoundedIcon fontSize="small" />, permission: 'reports' },
        { path: '/registrations', label: 'Registrations', icon: <ReceiptLongRoundedIcon fontSize="small" />, permission: 'registrations' },
        { path: '/tickets', label: 'Tickets', icon: <ConfirmationNumberRoundedIcon fontSize="small" />, permission: 'tickets' },
        { path: '/receipts', label: 'Receipts', icon: <ReceiptLongRoundedIcon fontSize="small" />, permission: 'receipts' },
        { path: '/manual-receipts', label: 'Manual Receipts', icon: <ReceiptLongRoundedIcon fontSize="small" />, permission: 'receipts' },
        { path: '/cancellations', label: 'Cancellations', icon: <CancelScheduleSendRoundedIcon fontSize="small" />, permission: 'cancellations' },
        {
          path: '/refund-calculator',
          label: 'Refund Calculator',
          icon: <CancelScheduleSendRoundedIcon fontSize="small" />,
          permission: 'cancellations_manage'
        }
      ]
    },
    {
      id: 'adventure',
      title: 'Adventure Management',
      items: [
        { path: '/treks', label: 'Treks', icon: <HikingRoundedIcon fontSize="small" />, permission: 'treks' },
        { path: '/event-batches', label: 'Event Batches', icon: <EventAvailableRoundedIcon fontSize="small" />, permission: 'event_batches' },
        { path: '/special-events', label: 'Special Events', icon: <EventAvailableRoundedIcon fontSize="small" />, permission: 'special_events' },
        { path: '/campsites', label: 'Campsites', icon: <CategoryRoundedIcon fontSize="small" />, permission: 'categories' },
        { path: '/event-types', label: 'Event Types', icon: <CategoryRoundedIcon fontSize="small" />, permission: 'categories' },
        { path: '/locations', label: 'Locations', icon: <CategoryRoundedIcon fontSize="small" />, permission: 'categories' },
        { path: '/attractions', label: 'Attractions', icon: <PlaceRoundedIcon fontSize="small" />, permission: 'content' },
        { path: '/add-ons', label: 'Add-ons', icon: <ReceiptLongRoundedIcon fontSize="small" />, permission: 'content' }
      ]
    },
    {
      id: 'store',
      title: 'Store & Rentals',
      items: [
        { path: '/store-products', label: 'Product List', icon: <ShoppingBagRoundedIcon fontSize="small" />, permission: 'store_products' },
        { path: '/store-stock', label: 'Stock & Inventory', icon: <CategoryRoundedIcon fontSize="small" />, permission: 'store_stock' },
        { path: '/store-returns', label: 'Returns & Approvals', icon: <HolidayVillageRoundedIcon fontSize="small" />, permission: 'store_returns' },
        { path: '/store-merchandise-orders', label: 'Merchandise Orders', icon: <LocalShippingRoundedIcon fontSize="small" />, permission: 'store_orders' },
        { path: '/store-camper-orders', label: "Camper's Store Orders", icon: <ConfirmationNumberRoundedIcon fontSize="small" />, permission: 'store_orders' }
      ]
    },
    {
      id: 'content',
      title: 'Content',
      items: [
        { path: '/content', label: 'Content / CMS', icon: <ArticleRoundedIcon fontSize="small" />, permission: 'content' },
        { path: '/site-labels', label: 'Site Labels', icon: <ShortTextRoundedIcon fontSize="small" />, permission: 'content' },
        { path: '/highlighted-events', label: 'Highlighted Events', icon: <EventAvailableRoundedIcon fontSize="small" />, permission: 'highlighted_events' },
        { path: '/home-categories', label: 'Home Categories', icon: <CategoryRoundedIcon fontSize="small" />, permission: 'home_categories' },
        { path: '/home-category-order', label: 'Home Category Order', icon: <CategoryRoundedIcon fontSize="small" />, permission: 'home_category_order' },
        { path: '/expedition', label: 'Expedition', icon: <EventAvailableRoundedIcon fontSize="small" />, permission: 'expedition' },
        { path: '/expedition-page', label: 'Expedition Page', icon: <EventAvailableRoundedIcon fontSize="small" />, permission: 'expedition_page' }
      ]
    },
    {
      id: 'pages',
      title: 'Pages',
      items: [
        { path: '/careers', label: 'Careers', icon: <WorkRoundedIcon fontSize="small" />, permission: 'careers' },
        { path: '/about', label: 'About Us', icon: <InfoRoundedIcon fontSize="small" />, permission: 'about' },
        { path: '/team', label: 'Team', icon: <GroupsRoundedIcon fontSize="small" />, permission: 'team' },
        { path: '/contact', label: 'Get In Touch', icon: <ContactMailRoundedIcon fontSize="small" />, permission: 'contact' }
      ]
    },
    {
      id: 'requests',
      title: 'Requests',
      items: [
        { path: '/applicants', label: 'Applicants', icon: <PersonSearchRoundedIcon fontSize="small" />, permission: 'applicants' },
        { path: '/volunteer-inquiries', label: 'Volunteer Inquiries', icon: <PersonSearchRoundedIcon fontSize="small" />, permission: 'volunteer_inquiries' },
        { path: '/lead-requests', label: 'Lead Requests', icon: <PhoneCallbackRoundedIcon fontSize="small" />, permission: 'lead_requests' },
        { path: '/lead-reports', label: 'Lead Reports', icon: <AssessmentRoundedIcon fontSize="small" />, permission: 'lead_reports' },
        { path: '/whatsapp-automation', label: 'WhatsApp Automation', icon: <WhatsAppIcon fontSize="small" />, permission: 'whatsapp_automation' },
        { path: '/short-links', label: 'Short Links (invcbl.in)', icon: <LinkRoundedIcon fontSize="small" />, permission: 'short_links' },
        { path: '/support-tickets', label: 'Support Tickets', icon: <SupportAgentRoundedIcon fontSize="small" />, permission: 'lead_requests' },
        { path: '/create-lead', label: 'Create Lead', icon: <NoteAddRoundedIcon fontSize="small" />, permission: 'lead_requests_create' }
      ]
    },
    {
      id: 'training_camp',
      title: 'Training Camp',
      items: [
        { path: '/training-camp/submissions', label: 'Candidate Submissions', icon: <HowToRegRoundedIcon fontSize="small" />, permission: 'registrations' },
        { path: '/training-camp/slots', label: 'Interview Slots', icon: <EventAvailableRoundedIcon fontSize="small" />, permission: 'registrations' }
      ]
    },
    {
      id: 'system',
      title: 'System',
      items: [
        { path: '/users', label: 'Users', icon: <GroupRoundedIcon fontSize="small" />, permission: 'users' },
        { path: '/admin-users', label: 'Admin Users', icon: <GroupRoundedIcon fontSize="small" />, permission: 'super_admin_only' },
        { path: '/role-builder', label: 'Role Builder', icon: <SecurityRoundedIcon fontSize="small" />, permission: 'users' },
        { path: '/activity-logs', label: 'Activity logs', icon: <HistoryRoundedIcon fontSize="small" />, permission: 'activity_logs' },
        { path: '/waplify-logs', label: 'WhatsApp Logs', icon: <WhatsAppIcon fontSize="small" />, permission: 'super_admin_only' }
      ]
    }
  ];

  const filterByPermission = (items) =>
    items.filter((item) => {
      if (!item.permission) return true;
      if (item.permission === 'super_admin_only' && !isSuper) return false;
      if (item.permission !== 'super_admin_only' && !can(item.permission)) return false;
      if (item.path === '/create-lead' && can('lead_requests')) return false;
      return true;
    });
  const visibleSections = menuSections
    .map((section) => ({ ...section, items: filterByPermission(section.items) }))
    .filter((section) => section.items.length > 0);

  const isActive = (path) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  const sidebarContent = (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: brand.paper,
        color: brand.secondary
      }}
    >
      <Box
        sx={{
          px: isCollapsed ? 1.25 : 2,
          py: 2,
          borderBottom: `1px solid ${brand.border}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: isCollapsed ? 'center' : 'space-between',
          gap: 1,
          minHeight: 72
        }}
      >
        {!isCollapsed && (
          <Box sx={{ minWidth: 0 }}>
            <Typography
              variant="subtitle1"
              sx={{
                color: brand.secondary,
                fontWeight: 800,
                letterSpacing: '-0.02em',
                lineHeight: 1.2
              }}
              noWrap
            >
              {brandName}
            </Typography>
            <Chip
              size="small"
              label={isSuper ? 'Super Admin' : user?.role || 'Admin'}
              sx={{
                mt: 0.75,
                height: 22,
                fontSize: 11,
                fontWeight: 700,
                bgcolor: isSuper ? 'rgba(222,88,36,0.14)' : '#f1f5f9',
                color: isSuper ? brand.primary : '#475569',
                border: `1px solid ${isSuper ? 'rgba(222,88,36,0.35)' : brand.border}`
              }}
            />
          </Box>
        )}
        {isDesktop ? (
          <IconButton
            onClick={onToggle}
            size="small"
            aria-label={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            sx={{
              color: '#475569',
              border: `1px solid ${brand.border}`,
              borderRadius: 2,
              bgcolor: '#fff'
            }}
          >
            {isCollapsed ? <ChevronRightRoundedIcon fontSize="small" /> : <ChevronLeftRoundedIcon fontSize="small" />}
          </IconButton>
        ) : null}
      </Box>

      <List sx={{ px: 1.25, py: 1.5, flex: 1, overflowY: 'auto' }} dense>
        {visibleSections.map((section, sectionIndex) => (
          <Box key={section.id} sx={{ mb: sectionIndex === visibleSections.length - 1 ? 0 : 0.5 }}>
            {!isCollapsed && (
              <Typography
                variant="caption"
                sx={{
                  display: 'block',
                  px: 1.25,
                  pb: 0.6,
                  pt: sectionIndex === 0 ? 0 : 0.5,
                  color: '#94a3b8',
                  textTransform: 'uppercase',
                  letterSpacing: '0.06em',
                  fontWeight: 700,
                  fontSize: 10.5
                }}
              >
                {section.title}
              </Typography>
            )}
            {section.items.map((item) => {
              const active = isActive(item.path);
              const button = (
                <ListItemButton
                  component={Link}
                  to={item.path}
                  onClick={() => setIsMobileOpen(false)}
                  selected={active}
                  sx={{
                    minHeight: 40,
                    borderRadius: 2,
                    px: isCollapsed ? 1 : 1.25,
                    justifyContent: isCollapsed ? 'center' : 'flex-start',
                    color: active ? brand.primary : '#334155',
                    '&.Mui-selected': {
                      bgcolor: brand.primarySoft,
                      color: brand.primary,
                      '&:hover': { bgcolor: 'rgba(222,88,36,0.16)' }
                    },
                    '&:hover': {
                      bgcolor: active ? 'rgba(222,88,36,0.16)' : 'rgba(15,23,42,0.04)'
                    }
                  }}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: isCollapsed ? 0 : 34,
                      color: 'inherit',
                      justifyContent: 'center'
                    }}
                  >
                    {item.icon}
                  </ListItemIcon>
                  {!isCollapsed && (
                    <ListItemText
                      primary={item.label}
                      primaryTypographyProps={{
                        fontSize: 13.5,
                        fontWeight: active ? 700 : 500,
                        noWrap: true
                      }}
                    />
                  )}
                </ListItemButton>
              );
              return (
                <ListItem key={item.path} disablePadding sx={{ mb: 0.35 }}>
                  {isCollapsed ? (
                    <Tooltip title={item.label} placement="right">
                      {button}
                    </Tooltip>
                  ) : (
                    button
                  )}
                </ListItem>
              );
            })}
            {sectionIndex < visibleSections.length - 1 && !isCollapsed ? (
              <Divider sx={{ mt: 1, mb: 1, borderColor: brand.border }} />
            ) : null}
          </Box>
        ))}
      </List>
    </Box>
  );

  return (
    <>
      {!isDesktop && (
        <IconButton
          onClick={() => setIsMobileOpen((v) => !v)}
          aria-label="Open menu"
          sx={{
            position: 'fixed',
            top: 12,
            left: 12,
            zIndex: 1400,
            bgcolor: '#fff',
            border: `1px solid ${brand.border}`,
            boxShadow: '0 4px 14px rgba(15,23,42,0.08)',
            '&:hover': { bgcolor: '#fff' }
          }}
        >
          <MenuRoundedIcon />
        </IconButton>
      )}
      <Drawer
        variant={isDesktop ? 'permanent' : 'temporary'}
        open={isDesktop ? true : isMobileOpen}
        onClose={() => setIsMobileOpen(false)}
        ModalProps={{ keepMounted: true }}
        sx={{
          width: isDesktop ? drawerWidth : undefined,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: drawerWidth,
            boxSizing: 'border-box',
            // Keep permanent drawer in document flow so main content sits flush (no double offset).
            position: isDesktop ? 'relative' : 'fixed',
            height: '100%',
            borderRight: `1px solid ${brand.border}`,
            transition: 'width 200ms ease-in-out',
            overflowX: 'hidden',
            boxShadow: isDesktop ? 'none' : '0 12px 40px rgba(15,23,42,0.18)'
          }
        }}
      >
        {sidebarContent}
      </Drawer>
    </>
  );
};

export default Sidebar;
